import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Rocket } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

export const meta = {
  id: 'recent_shipped',
  category: 'news',
  titleKey: 'dashboard.widgets.recentShipped',
  descriptionKey: 'dashboard.widgets.recentShippedDesc',
  defaultSize: 'md',
  icon: Rocket,
};

// Shows the latest "Shipped by Claude" entries from the feedback board, which
// is what powers the Changelog / Novedades page. Lets Andy notice new
// features without leaving the dashboard.
export default function RecentShippedWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const locale = user?.language === 'en' ? enUS : es;

  const { data: shipped = [] } = useQuery({
    queryKey: ['feedback-claude-shipped'],
    queryFn: () => api.get('/feedback', { params: { source: 'claude_shipped' } }).then(r => r.data),
    refetchInterval: 5 * 60 * 1000,
  });

  const recent = (Array.isArray(shipped) ? shipped : shipped?.requests || []).slice(0, 6);

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', height: '100%' }}>
      <div className="card-header" style={{ padding: '14px 20px', borderBottom: '1px solid var(--c-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Rocket size={16} />
          {t('dashboard.widgets.recentShipped')}
        </div>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/changelog')}>
          {t('dashboard.widgets.viewChangelog', 'Changelog')} →
        </button>
      </div>
      {recent.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--c-text-muted)', fontSize: 13 }}>—</div>
      ) : (
        recent.map(item => (
          <div
            key={item.id}
            onClick={() => navigate('/changelog')}
            style={{
              padding: '10px 16px',
              borderBottom: '1px solid var(--c-border-subtle)',
              cursor: 'pointer',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = 'var(--c-surface-2)')}
            onMouseLeave={e => (e.currentTarget.style.background = '')}
          >
            <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {item.title}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3, fontSize: 11, color: 'var(--c-text-muted)' }}>
              {item.module_key && (
                <span style={{
                  padding: '1px 6px', borderRadius: 999,
                  background: 'rgba(59, 130, 246, 0.12)', color: 'var(--c-brand)', fontWeight: 600,
                }}>
                  {item.module_key}
                </span>
              )}
              <span>{formatDistanceToNow(new Date(item.created_at), { addSuffix: true, locale })}</span>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
